import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Smartphone, Zap, WifiOff, BellRing, X, Share, Check } from "lucide-react";
import { isNoldDomain } from "../lib/domain";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}
function isMobile() {
  return /iphone|ipad|ipod|android/i.test(window.navigator.userAgent) || window.innerWidth < 768;
}

export function InstallApp() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setGuide(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else {
      setGuide(true);
    }
  };

  const benefits = [
    { icon: Zap, label: t("install.b1") },
    { icon: WifiOff, label: t("install.b2") },
    { icon: BellRing, label: t("install.b3") },
  ];

  const steps = isIOS()
    ? { title: t("install.iosTitle"), items: [t("install.iosStep1"), t("install.iosStep2"), t("install.iosStep3")] }
    : { title: t("install.androidTitle"), items: [t("install.androidStep1"), t("install.androidStep2"), t("install.androidStep3")] };

  return (
    <section id="install" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24" data-testid="install-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-[2rem] bg-[#1A1A1A] text-white p-8 md:p-14 relative overflow-hidden grid lg:grid-cols-2 gap-10 items-center"
      >
        <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[#F26419]/20 blur-2xl" />
        <div className="absolute right-10 top-10 w-40 h-40 rounded-full bg-[#F26419]/10 blur-2xl" />

        <div className="relative">
          <p className="text-[#F26419] text-sm font-semibold uppercase tracking-[0.1em]">{t("install.kicker")}</p>
          <h2 className="mt-3 font-head font-black text-3xl sm:text-4xl tracking-tight">{t("install.secTitle")}</h2>
          <p className="mt-4 text-white/70 text-base sm:text-lg max-w-md leading-relaxed">{isNoldDomain() ? t("install.secText").replace(/BentSkin\s*Go/gi, "Nold Go").replace(/Ben-?skin/gi, "trajet").replace(/moto/gi, "voiture") : t("install.secText")}</p>

          <ul className="mt-7 space-y-3">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-center gap-3 text-white/85">
                <span className="w-9 h-9 shrink-0 rounded-xl bg-white/10 flex items-center justify-center">
                  <b.icon className="w-4 h-4 text-[#F26419]" />
                </span>
                <span className="font-medium">{b.label}</span>
              </li>
            ))}
          </ul>

          <div className="mt-9">
            {installed ? (
              <div data-testid="install-done" className="inline-flex items-center gap-2 h-14 px-7 rounded-2xl bg-[#008955] text-white font-bold text-lg">
                <Check className="w-5 h-5" /> {t("install.installedOk")}
              </div>
            ) : (
              <button
                data-testid="install-app-btn"
                onClick={handleInstall}
                className="group h-16 px-8 rounded-2xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-lg inline-flex items-center gap-3 transition-transform hover:-translate-y-1 shadow-xl shadow-black/30"
              >
                <Download className="w-6 h-6" /> {t("install.installNow")}
              </button>
            )}
            {!installed && !isMobile() && (
              <p className="mt-4 text-white/50 text-sm flex items-center gap-2">
                <Smartphone className="w-4 h-4" /> {t("install.desktopHint")}
              </p>
            )}
          </div>
        </div>

        {/* Phone mockup */}
        <div className="relative hidden lg:flex justify-center">
          <div className="w-[220px] h-[440px] rounded-[2.5rem] border-[10px] border-[#2A322D] bg-[#FDF8F0] shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center">
              <div className="w-24 h-5 bg-[#2A322D] rounded-b-2xl" />
            </div>
            <div className="pt-10 px-4">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
                  <Download className="w-4 h-4 text-white" />
                </span>
                <span className="font-head font-extrabold text-[#1A1A1A]">{isNoldDomain() ? "Nold" : "BentSkin"}<span className="text-[#F26419]"> Go</span></span>
              </div>
              <div className="mt-4 h-28 rounded-2xl bg-gradient-to-br from-[#F26419] to-[#D94F2B]" />
              <div className="mt-3 h-3 w-3/4 rounded-full bg-[#E2DFD8]" />
              <div className="mt-2 h-3 w-1/2 rounded-full bg-[#E2DFD8]" />
              <div className="mt-4 h-11 rounded-xl bg-[#F26419]" />
              <div className="mt-3 h-11 rounded-xl bg-[#F26419]" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Guided steps modal */}
      <AnimatePresence>
        {guide && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setGuide(false)}
            data-testid="install-guide-modal"
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 320 }}
              className="bg-white rounded-3xl w-full max-w-md p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="install-guide-close"
                onClick={() => setGuide(false)}
                className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-[#F0EBE1] flex items-center justify-center"
              >
                <X className="w-5 h-5 text-[#75807A]" />
              </button>
              <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
                {isIOS() ? <Share className="w-6 h-6 text-white" /> : <Smartphone className="w-6 h-6 text-white" />}
              </span>
              <h3 className="mt-4 font-head font-bold text-xl text-[#1A1A1A]">{steps.title}</h3>
              <ol className="mt-5 space-y-4">
                {steps.items.map((s, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[#F26419] text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                    <span className="text-[#3B423E] leading-relaxed pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
              <button
                data-testid="install-guide-gotit"
                onClick={() => setGuide(false)}
                className="mt-7 w-full py-3.5 rounded-2xl bg-[#1A1A1A] text-white font-bold"
              >
                {t("install.gotIt")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
